import type {
  IFixedJackpotEventRequest,
  IFixedJackpotEvent,
  IFixedJackpotPrizeRequest,
  IFixedJackpotPrize,
} from '~/interfaces/jackpots/fixed';
import type { IProgressiveJackpotResponse } from '~/interfaces/jackpots/progressive';
import { useAuthStore } from '~/stores/auth.store';
import { useGameStore } from '~/stores/games.store';

export const useJackpotService = () => {
  const { $fetch } = useNuxtApp();
  const gameStore = useGameStore();
  const auth = useAuthStore();
  const fixedJackpots = useFixedJackpots();

  const fetchProgressiveJackpots =
    async (): Promise<IProgressiveJackpotResponse> => {
      try {
        return await $fetch('api/v1/Gaming/ProgressiveJackpots', {
          base: 'baseC',
          useRegionCode: true,
        });
      } catch (e) {
        console.error(e);
      }
    };

  const fetchFixedJackpotEvents = async (
    params?: IFixedJackpotEventRequest,
  ): Promise<IFixedJackpotEvent[]> => {
    try {
      return await $fetch<IFixedJackpotEvent[]>(
        'api/v1/Gaming/FixedJackpot/Events',
        {
          base: 'base',
          params,
          useAuthentication: auth.isLoggedIn,
          useRegionCode: true,
          useEnvironment: true,
          async onResponse({ response }) {
            const data: IFixedJackpotEvent[] = response._data || [];
            if (data.length > 0) {
              const transformData = data?.map((jackpot) => {
                return {
                  ...jackpot,
                  eventId: jackpot.id,
                  activeFromDateTime: new Date(jackpot.activeFromDateTime),
                  activeToDateTime: new Date(jackpot.activeToDateTime),
                  gameCategory: fixedJackpots?.mapFixedJackpotCategories(
                    jackpot.gameCategory,
                  ),
                };
              });
              gameStore.setFixedJackpot(transformData);
              response._data = transformData;
            } else {
              response._data = [];
              gameStore.setFixedJackpot([]);
            }
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFixedJackpotPrize = async (
    params: IFixedJackpotPrizeRequest,
  ): Promise<IFixedJackpotPrize> => {
    try {
      return await $fetch('api/v1/Gaming/FixedJackpot/Prize', {
        base: 'base',
        params,
        useRegionCode: true,
        useEnvironment: true,
        async onResponse({ response }) {
          const data: IFixedJackpotPrize = response._data;
          if (!!data) {
            response._data = {
              ...data,
              activeFromDateTime: new Date(data.activeFromDateTime),
              activeToDateTime: new Date(data.activeToDateTime),
            };
          } else response._data = null;
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    fetchProgressiveJackpots,
    fetchFixedJackpotEvents,
    fetchFixedJackpotPrize,
  };
};
